import { useEffect } from 'react';
import { Control, Controller, FormProvider, SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import Button from '@components/common/button';
import { useToast } from '@components/common/toast';
import { UploadFile } from '@components/common/upload';
import { JDModalType } from '@components/position/jd/apply/modals';
import { useAdditionalItems, useSaveAdditionalInfo } from '@repository/match';
import Text from '@components/position/jd/apply/modals/additionalInputModal/Text';
import SingleChoice from '@components/position/jd/apply/modals/additionalInputModal/SingleChoice';
import MultiChoice from '@components/position/jd/apply/modals/additionalInputModal/MultiChoice';
import AttachFile from '@components/position/jd/apply/modals/additionalInputModal/AttachFile';

import Dropdown from '@components/position/jd/apply/modals/additionalInputModal/Dropdown';
import LeaveModal from '@components/position/jd/apply/modals/LeaveModal';
import ResponsiveModal from '@components/_atoms/ResponsiveModal';
import { convertFormInitValue, parsingFormData } from './convertUtil';
import { AdditionInfoVO, AnswerVO } from '@domain/vo/match/ResumeAdditionInfoVO';
import { AdditionalItemType } from '@domain/constant/profileRequirementType';
import useModal from '@utils/hooks/useModal';

import { useKeyDown } from '@utils/hooks/useKeyboard';
import classnames from 'classnames/bind';
import styles from './additionalInputModal.module.scss';

const cx = classnames.bind(styles);

export type FormDataType = Record<number, string | string[] | { label: string; value: string } | UploadFile[]>;

interface AdditionalInputItemProps extends AdditionInfoVO {
  control: Control;
}

const AdditionalInputItem = (props: AdditionalInputItemProps) => {
  switch (props.formType) {
    case 'TEXT':
      return <Text {...props} />;
    case 'SINGLE_CHOICE':
      return <SingleChoice {...props} />;
    case 'MULTI_CHOICE':
      return <MultiChoice {...props} />;
    case 'DROPDOWN':
      return <Dropdown {...props} />;
    case 'ADDITION_ATTACH_FILE':
      return <AttachFile {...props} />;
  }
};

interface Props {
  visible: boolean;
  setModalState: (modalState: JDModalType) => void;
  positionSn: number;
}

const AdditionalInputModal = ({ visible, setModalState, positionSn }: Props) => {
  const Toast = useToast();
  const methods = useForm<FormDataType>({
    mode: 'onChange',
  });
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { isDirty },
  } = methods;

  const { data: additionalItem, refetch } = useAdditionalItems(positionSn);
  const { closeModal: closeLeaveModal, isModalOpen, openModal: openLeaveModal } = useModal();
  const { mutateAsync: saveAdditionalInfo } = useSaveAdditionalInfo();

  const handleClickPrevButton = () => {
    if (isDirty) {
      openLeaveModal();
    } else {
      setModalState('EASY_APPLY');
    }
  };

  const handleClickLeaveButton = () => {
    reset({});
    closeLeaveModal();
    setModalState('EASY_APPLY');
  };

  const onSubmit: SubmitHandler<FormDataType> = (data) => {
    if (!additionalItem) return;
    const convertRq = parsingFormData({ formData: data, additionInfo: additionalItem.additionInfo });
    saveAdditionalInfo(
      { positionSn, additionInfo: convertRq },
      {
        onSuccess: async () => {
          await refetch();
          Toast({ content: '성공적으로 저장되었습니다.', type: 'success', iconType: 'check' });
        },
      },
    );
    setModalState('EASY_APPLY');
  };

  const onInvalid: SubmitErrorHandler<FormDataType> = (_error) => {
    if (!additionalItem) return;
    const convertRq = parsingFormData({ formData: getValues(), additionInfo: additionalItem.additionInfo });
    saveAdditionalInfo(
      { positionSn, additionInfo: convertRq },
      {
        onSuccess: async () => {
          await refetch();
          Toast({ content: '성공적으로 저장되었습니다.', type: 'success', iconType: 'check' });
        },
      },
    );
    setModalState('EASY_APPLY');
  };

  useKeyDown(() => {
    visible && handleClickPrevButton();
  }, 'Escape');

  useEffect(() => {
    if (!additionalItem) return;
    const saveAnswers = additionalItem?.additionInfo.map((item) => item.answer);

    if (!saveAnswers?.length) return;
    const validSaveAnswers = saveAnswers.filter((item): item is AnswerVO => item !== null);
    const convertInitValue = convertFormInitValue(validSaveAnswers);
    reset(convertInitValue);
  }, [additionalItem]);

  return (
    <FormProvider {...methods}>
      <ResponsiveModal visible={visible} dimmed={false}>
        <section className={cx('layer')}>
          <h2 className={cx('title')}>추가 입력</h2>

          <form className={cx('formWrapper')} onSubmit={handleSubmit(onSubmit, onInvalid)}>
            <ResponsiveModal.ScrollArea maxHeight={495}>
              <div className={cx('formArea')}>
                {additionalItem?.additionInfo.map((item) => (
                  <Controller
                    key={item.additionInfoSn}
                    name={`${item.additionInfoSn}` as never}
                    control={control}
                    rules={{ required: item.required ? getErrorMessage(item.formType) : false }}
                    render={({ field }) => (
                      <AdditionalInputItem
                        {...item}
                        key={item.additionInfoSn}
                        required={item.required}
                        {...field}
                        control={control}
                      />
                    )}
                  />
                ))}
              </div>
            </ResponsiveModal.ScrollArea>

            <div className={cx('btnArea')}>
              <Button className={cx('btn')} onClick={handleClickPrevButton} theme="secondary" type="button">
                이전
              </Button>
              <Button className={cx('btn')} type="submit">
                저장
              </Button>
            </div>
          </form>
        </section>

        {/* 입력 후 나가시겠어요? 모달 */}
        <LeaveModal visible={isModalOpen} onClose={closeLeaveModal} onOk={handleClickLeaveButton} />
      </ResponsiveModal>
    </FormProvider>
  );
};

export default AdditionalInputModal;

const getErrorMessage = (formType: AdditionalItemType) => {
  switch (formType) {
    case 'TEXT':
      return '내용을 입력해 주세요.';
    case 'SINGLE_CHOICE':
    case 'MULTI_CHOICE':
    case 'DROPDOWN':
      return '내용을 선택해 주세요.';
    case 'ADDITION_ATTACH_FILE':
      return '첨부 파일을 추가해주세요.';
    default:
      return '';
  }
};
